import { useAuth } from '../contexts/AuthContext'
import { useActiveMarkedsomrade } from './useActiveMarkedsomrade'
import { useMineMarkedsomrader } from './useMineMarkedsomrader'

/**
 * Returns the postnumre comma-string the analytics endpoints should filter on,
 * or null when no filter should be applied.
 *
 * Resolution order when filterEnabled = true:
 *   1. Active markedsområde (selected via UI, persisted in localStorage)
 *   2. Legacy user.mine_postnummer
 *
 * The hook signature is unchanged from the previous version, so every page that
 * already calls `useMinePostnummer(filterEnabled)` keeps working without edit.
 */
export function useMinePostnummer(filterEnabled: boolean): string | null {
  return useMinePostnummerInfo(filterEnabled).postnummer
}

export interface MinePostnummerInfo {
  /** Comma-separated postnumre for the API, or null. */
  postnummer: string | null
  /**
   * Name of the markedsområde the postnumre came from, or null when they came
   * from the legacy user.mine_postnummer (which has no name).
   *
   * #527 — the explanation box printed every single postnummer; an område can
   * hold hundreds. It shows this name instead when there is one.
   */
  navn: string | null
}

/**
 * Same resolution as useMinePostnummer, but also reports WHICH markedsområde
 * the codes came from. Both hooks share this one implementation, so a caller
 * that needs the name does not pay for a second lookup.
 */
export function useMinePostnummerInfo(filterEnabled: boolean): MinePostnummerInfo {
  const { user } = useAuth()
  const { activeId } = useActiveMarkedsomrade()
  const { items } = useMineMarkedsomrader()

  if (!filterEnabled) return { postnummer: null, navn: null }

  if (activeId !== null) {
    const active = items.find(i => i.id === activeId)
    if (active && active.postnumre.length > 0) {
      return { postnummer: active.postnumre.join(','), navn: active.name }
    }
  }

  return { postnummer: user?.mine_postnummer ?? null, navn: null }
}
